import type { PublicWorkOrder } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, post } from "../api";
import { fromLocalInput } from "../components/schedule";
import { Alert, Button, Field, Loading, TextAreaField, formatDateTime } from "../components/ui";
import { LinesTable } from "./WorkOrders";
import { RichText } from "../components/rich-text";

/** The sub's work order page: no account, the link is the key. Confirm, decline, or propose a new time. */
export function PublicWorkOrderPage() {
  const token = window.location.pathname.split("/")[2] ?? "";
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["public-wo", token], queryFn: () => get<PublicWorkOrder>(`/api/public/work-orders/${token}`), retry: false });
  const set = (d: PublicWorkOrder) => qc.setQueryData(["public-wo", token], d);

  useEffect(() => {
    if (q.data?.company.primaryColor) document.documentElement.style.setProperty("--nmt-primary", q.data.company.primaryColor);
  }, [q.data?.company.primaryColor]);

  if (q.isLoading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Loading />
      </div>
    );
  }
  if (q.error || !q.data) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <Alert>{errorMessage(q.error) || "This link isn't valid."}</Alert>
      </div>
    );
  }
  const w = q.data;
  return (
    <div className="min-h-dvh bg-paper">
      <header className="bg-[var(--color-sidebar)] text-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4">
          <div className="flex items-center gap-3">
            {w.company.logoUrl && <img src={w.company.logoUrl} alt="" className="h-9 w-auto rounded bg-white p-1" />}
            <span className="text-lg font-semibold">{w.company.name}</span>
          </div>
          <a href={`tel:${w.company.phone}`} className="text-sm text-white/80 hover:text-white">
            {w.company.phone}
          </a>
        </div>
      </header>
      <main className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-6">
        <div>
          <h1 className="text-2xl font-semibold">Work order {w.number}</h1>
          <p className="text-ink-soft">
            For {w.subName} · from {w.contactName}
          </p>
        </div>
        <WorkOrderCard w={w} respondUrl={`/api/public/work-orders/${token}/respond`} onDone={set} />
      </main>
    </div>
  );
}

const time = (a: string | null, b: string | null) => (a ? `${formatDateTime(a)}${b ? ` – ${formatDateTime(b)}` : ""}` : "To be scheduled");

/** A work order for the sub: details, and their answer (from its own link or their portal). */
export function WorkOrderCard({ w, respondUrl, onDone }: { w: PublicWorkOrder; respondUrl: string; onDone: (d: PublicWorkOrder) => void }) {
  return (
    <>
        <section className="flex flex-col gap-3 rounded-[var(--radius-panel)] bg-white px-5 py-5 text-sm shadow-[var(--shadow-card)]">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
            <dt className="text-muted">When</dt>
            <dd className="font-semibold">{time(w.startsAt, w.endsAt)}</dd>
            <dt className="text-muted">Where</dt>
            <dd>
              {w.address ? (
                <a href={`https://maps.google.com/?q=${encodeURIComponent(w.address)}`} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                  {w.address}
                </a>
              ) : (
                "—"
              )}
            </dd>
          </dl>
          {w.title && <h2 className="text-base font-semibold">{w.title}</h2>}
          {w.scope && (
            <div>
              <div className="font-semibold text-ink-soft">Scope of work</div>
              <RichText text={w.scope} />
            </div>
          )}
          {w.materials && (
            <div>
              <div className="font-semibold text-ink-soft">Materials</div>
              <RichText text={w.materials} />
            </div>
          )}
          {w.lines.length > 0 && <LinesTable lines={w.lines} totalCents={w.totalCents} />}
          {w.pdfUrl && (
            <a href={w.pdfUrl} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline">
              Download the work order (PDF)
            </a>
          )}
        </section>
        {w.status === "confirmed" ? (
          <>
            <Alert tone="ok">You confirmed this work order. Thanks! Call {w.company.phone} if anything changes.</Alert>
            <Respond url={respondUrl} w={w} onDone={onDone} changing />
          </>
        ) : w.status === "proposed" ? (
          <>
            <Alert tone="info">You proposed {time(w.proposedStartsAt, w.proposedEndsAt)}. We'll get back to you.</Alert>
            <Respond url={respondUrl} w={w} onDone={onDone} changing />
          </>
        ) : w.status === "declined" ? (
          <Alert tone="info">You declined this work order. If that changes, call {w.company.phone}.</Alert>
        ) : w.status === "sent" ? (
          <Respond url={respondUrl} w={w} onDone={onDone} />
        ) : (
          <Alert tone="info">This work order is {w.status}.</Alert>
        )}
    </>
  );
}

function Respond({ url, w, onDone, changing }: { url: string; w: PublicWorkOrder; onDone: (d: PublicWorkOrder) => void; changing?: boolean }) {
  const [mode, setMode] = useState<"" | "decline" | "propose">("");
  const [note, setNote] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const send = useMutation({
    mutationFn: (answer: "confirm" | "decline" | "propose") =>
      post<PublicWorkOrder>(url, answer === "propose" ? { answer, note, startsAt: fromLocalInput(start), endsAt: fromLocalInput(end) } : { answer, note }),
    onSuccess: (d) => {
      setMode("");
      setNote("");
      onDone(d);
    },
  });
  return (
    <section className="flex flex-col gap-4 rounded-[var(--radius-panel)] bg-white px-5 py-5 shadow-[var(--shadow-card)]">
      <h2 className="text-lg font-semibold">{changing ? "Need to change your answer?" : "Can you do it?"}</h2>
      {send.error && <Alert>{errorMessage(send.error)}</Alert>}
      <div className="flex flex-wrap gap-2">
        {w.status !== "confirmed" && (
          <Button variant="primary" busy={send.isPending && send.variables === "confirm"} onClick={() => send.mutate("confirm")}>
            {w.status === "proposed" ? "The original time works after all" : w.startsAt ? "Confirm this time" : "Confirm"}
          </Button>
        )}
        <Button onClick={() => setMode(mode === "propose" ? "" : "propose")}>Propose a different time</Button>
        <Button variant="quiet" onClick={() => setMode(mode === "decline" ? "" : "decline")}>
          Decline
        </Button>
      </div>
      {mode === "propose" && (
        <div className="flex flex-col gap-3 border-t border-mist pt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
            <Field label="End" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <TextAreaField label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          <div>
            <Button variant="primary" busy={send.isPending} disabled={!start || !end} onClick={() => send.mutate("propose")}>
              Send proposed time
            </Button>
          </div>
        </div>
      )}
      {mode === "decline" && (
        <div className="flex flex-col gap-3 border-t border-mist pt-4">
          <TextAreaField label="Reason (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          <div>
            <Button busy={send.isPending} onClick={() => send.mutate("decline")}>
              Decline this work order
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
